import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    inject,
    Input,
    OnDestroy,
    OnInit,
    Output
} from "@angular/core";
import { FormArray, FormBuilder, FormControl, FormGroup } from "@angular/forms";
import SpatialReference from "@arcgis/core/geometry/SpatialReference";
import { clone } from "lodash";
import {
    combineLatest,
    Observable,
    Subject,
    takeUntil,
    tap
} from "rxjs";
import { SelectedListItem } from "~app/common/components/list-selected-items/listSelectedItems.models";
import { MapImportService } from "~app/common/components/map/components/menu/map-file-import/map-import.service";
import { unionOperator } from "~app/common/components/map/geometry-operators";
import { GeometryType, IMapComponent } from "~app/common/components/map/map.models";
import { IMapGeometry, IMapGeometryWicket } from "~app/common/components/map/map.settings";
import { TerraformerService } from "~app/common/components/map/services/terraformer.service";
import { Subtyp1, Subtyp2 } from "~app/common/components/map/symbols/kartgeometri.constants";
import { Colors } from "~app/common/components/map/symbols/symbol.constants";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { VkKartaLayerNames } from "~app/common/models/constants";
import { RaskCommonModule } from "~app/common/rask-common.module";
import { GuidUtils } from "~app/common/utils/guid-utils";
import { IKartgeometriBasdataResponse } from "~app/features/map/popups/kartgeometri-popup/kartgeometri.model";
import { KartaService } from "~app/features/planera/services/karta.service";
import { Atgard } from "../../models/atgard-models";
import { KontraktMediatorService } from "../../services/kontrakt-mediator.service";
import { FileSelectDialogComponent } from "./file-select-dialog/file-select-dialog.component";
import { GenericFeatureInfo, ImportedGeometry, ImportSelectData } from "./import-geometries-models";

type MapForm = FormGroup<{
    visible: FormControl<boolean>;
    name: FormControl<string>;
    type: FormControl<string>;
    typeLabel: FormControl<string>;
    wkt: FormControl<string>;
    destination: FormControl<ImportSelectData>;
    atgard: FormControl<string>;
    attributes: FormArray<AttributeForm>;
    id: FormControl<string>;
}>;

type AttributeForm = FormGroup<{
    key: FormControl<string>;
    value: FormControl<string>;
}>;

type ImportForm = FormGroup<{
    mappings: FormArray<MapForm>;
}>;

@Component({
    selector: "rask-import-geometries-popup",
    imports: [RaskCommonModule],
    templateUrl: "./import-geometries-popup.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: "./import-geometries-popup.component.scss"
})
export class ImportGeometriesPopupComponent implements OnInit, OnDestroy {

    @Output() readonly import = new EventEmitter<ImportedGeometry[]>();
    protected atgarder$: Observable<Atgard[]>;
    protected typerPunkt: ImportSelectData[] = [];
    protected typerLinje: ImportSelectData[] = [];
    protected typerYta: ImportSelectData[] = [];
    protected form: ImportForm;
    protected visible = false;

    private readonly mapImportService = inject(MapImportService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly kartaService = inject(KartaService);

    private readonly fb = inject(FormBuilder);
    private readonly terraformerService = inject(TerraformerService);
    private readonly modalDialogService = inject(RaskModalService);

    private readonly mapChangeSrc = new Subject<IMapComponent>();
    private readonly ngUnsubscribe$ = new Subject<void>();

    private features: GenericFeatureInfo[] = [];

    @Input()
    get map(): IMapComponent {
        return this.mapInternal;
    }
    set map(value: IMapComponent) {
        this.mapInternal = value;
        this.mapChangeSrc.next(value);
    }
    private mapInternal: IMapComponent;

    get mappings() {
        return this.form.controls.mappings;
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    ngOnInit(): void {
        this.form = this.fb.group({
            mappings: this.fb.array<MapForm>([])
        });

        this.mapImportService.fileImport$.pipe(
            tap((features) => this.handleFileImported(features)),
            takeUntil(this.ngUnsubscribe$)
        ).subscribe();

        this.mapImportService.closeFileImport$.pipe(
            tap(() => this.close()),
            takeUntil(this.ngUnsubscribe$)
        ).subscribe();

        this.kartaService.getKartgeometriBasdataPunkter().subscribe(responsepunkter => {
            this.typerPunkt = responsepunkter.map(punkt => this.mapBasdataToKartgeometriMapping("kontrakt", punkt)).flat();
            Subtyp2.AllmanPunktSubtyp2Lista.forEach(subtyp2 => {
                this.typerPunkt.push(new ImportSelectData("kontrakt", Subtyp1.allmanpunkt, subtyp2));
            });
        });

        this.kartaService.getKartgeometriBasdataLinjer().subscribe(responseLinjer => {
            this.typerLinje = responseLinjer.map(linje => this.mapBasdataToKartgeometriMapping("kontrakt", linje)).flat();
            Subtyp2.AllmanLinjeSubtyp2Lista.forEach(subtyp2 => {
                this.typerLinje.push(new ImportSelectData("kontrakt", Subtyp1.allmanlinje, subtyp2));
            });
        });

        this.atgarder$ = this.kontraktMediatorService.atgarder$;
        combineLatest([
            this.atgarder$,
            this.kartaService.getKartgeometriBasdataYtor()
        ]).pipe(
            takeUntil(this.ngUnsubscribe$)
        ).subscribe(([
            atgarder,
            basdataYtor
        ]) => {
            const ytor = [];
            const ejTillgangligaAtg = atgarder.filter(atg => !atg.isTillgangligtOrSenare);

            ejTillgangligaAtg.filter(atg => !atg.isSkogsvard).forEach(atg => {
                ytor.push({
                    geometryType: "atgard",
                    subtyp1: "Åtgärd",
                    subtyp2: atg.beteckning,
                    subtyp3: atg.atgardstyp,
                    atgGuid: atg.guid
                });
            });

            ejTillgangligaAtg.filter(atg => atg.isSkogsvard && !Atgard.isPlantleverans(atg.atgardstyp)).forEach(atg => {
                ytor.push({
                    geometryType: "atgard",
                    subtyp1: "Skogsvårdsåtgärd",
                    subtyp2: atg.beteckning,
                    subtyp3: atg.atgardstyp,
                    atgGuid: atg.guid
                });
            });

            ytor.push(...basdataYtor.map(yta => this.mapBasdataToKartgeometriMapping("kontrakt", yta)).flat());
            this.typerYta = ytor;
        });

    }

    protected genericItemFormatter = (x: ImportSelectData) => x;
    protected genericViewFormatter = (x: ImportSelectData) => x ? this.getSelectName(x) : undefined;

    protected onImportClick() {
        this.hideAllGeometries();

        let geometries = this.mappings.controls
            .filter(m => m.controls.destination.value)
            .map<ImportedGeometry>(this.mapFormToGeometry);
        const spatialRef = new SpatialReference({ wkid: 3006 });

        const groupedGeometries = geometries.reduce((acc, item) => {
            if (item.atgardGuid) {
                if (!Object.hasOwn(acc, item.atgardGuid)) {
                    acc[item.atgardGuid] = [];
                }
                acc[item.atgardGuid].push(item);
            }
            return acc;
        }, {} as Record<string, ImportedGeometry[]>);

        const multipleGeometriesPerAtgard = Object.entries(groupedGeometries).filter(
            ([
                _,
                geometriesGroup
            ]) => geometriesGroup.length > 1
        );
        for (const [
            atgardGuid,
            geometriesGroup
        ] of multipleGeometriesPerAtgard) {
            const newGeometry = clone(geometries.filter(r => r.atgardGuid === atgardGuid)[0]);
            geometries = geometries.filter(r => r.atgardGuid !== atgardGuid);

            const arcgisGeometries = geometriesGroup.map(g => this.terraformerService.wktToArcgis(g.wktGeometry, spatialRef));
            const arcgisUnionGeom = unionOperator.executeMany(arcgisGeometries);

            newGeometry.wktGeometry = this.terraformerService.arcGisToWkt(arcgisUnionGeom);
            geometries.push(newGeometry);
        }

        this.visible = false;
        this.import.emit(geometries);
    }

    protected hoverRow(importGeometriId: string, mouseover: boolean) {
        // eslint-disable-next-line sonarjs/no-selector-parameter
        if (mouseover && this.features.find(f => f.id === importGeometriId).visible) {
            this.map?.highlightGeometries(VkKartaLayerNames.importLayer, [{ id: importGeometriId }]);
        } else {
            this.map?.clearAllHighlights();
        }
    }

    protected updateVisibleRows(form: MapForm) {
        this.features.find(f => f.id === form.controls.id.value).visible = form.controls.visible.value;
        this.updateVisibleGeometries(false);
    }

    protected isTypePunkt(type: string) {
        return type === GeometryType.point || type === GeometryType.multipoint;
    }
    protected isTypeLinje(type: string) {
        return type === GeometryType.linestring || type === GeometryType.multilinestring;
    }
    protected isTypeYta(type: string) {
        return type === GeometryType.polygon || type === GeometryType.multipolygon;
    }

    protected close() {
        this.hideAllGeometries();
        this.visible = false;
    }

    protected summaryText(): string {
        const selected = this.mappings.controls.filter(m => m.controls.destination.value).length;
        const totalAmount = this.mappings.controls.length;
        return `${selected} av ${totalAmount} geometrier valda`;
    }

    private getSelectName(geometriMapping: ImportSelectData): string {
        if (geometriMapping.subtyp2) {
            if (geometriMapping.subtyp3) {
                return `${geometriMapping.subtyp1} - ${geometriMapping.subtyp2} - ${geometriMapping.subtyp3}`;
            } else {
                return `${geometriMapping.subtyp1} - ${geometriMapping.subtyp2}`;
            }
        }
        return geometriMapping.subtyp1;
    }

    private handleFileImported(features: GenericFeatureInfo[]) {
        features = this.filterEndsWithEmpty(features);
        const uniqueFilenames = [...new Set(features.map(item => item.name))];
        this.modalDialogService.open("Välj filer", FileSelectDialogComponent, "md", true, {
            filer: uniqueFilenames,
            callback: this.importCallback,
            features: features
        });
    }

    private readonly importCallback = (selectedFiles: SelectedListItem[], features: GenericFeatureInfo[]): void => {
        this.visible = true;

        this.features = this.processAndSort(features.filter(x => selectedFiles.some(y => y.beteckning === x.name)));

        this.features.forEach(f => f.mapColor = this.getColorForFeature(f.geometryType));
        this.mappings.clear();
        this.features.forEach(f => this.addMapping(f));
        this.updateVisibleGeometries(true);
    };

    //
    // Exempel på sorteringsordning
    //
    // Synlig | Namn    | Typ
    // Ja     | Trakt   | Yta
    // Ja     | Hänsyn  | Yta
    // Ja     | Linjer  | Linje
    // Ja     | Avlägg  | Punkt
    // Nej     | fastighetsytor  | Yta
    private processAndSort(features: GenericFeatureInfo[]) {
        // Heurustik för att hantera sorteringsordning av geometrier
        // Om det finns features med namn "trakt" så sorteras dom högst upp inom egen typ
        // Lager vid namn fastighetsytor döljs per default och placeras längst ned
        const geometryTypeLevelBase = 1000;

        const getPriority = (type: string): number => {
            if (this.isTypePunkt(type)) {
                return 1 * geometryTypeLevelBase;
            } else if (this.isTypeLinje(type)) {
                return 2 * geometryTypeLevelBase;
            } else if (this.isTypeYta(type)) {
                return 3 * geometryTypeLevelBase;
            }
            return 0;
        };

        // Positiv boost => högre prioritet, negativ boost => lägre prioritet
        // Sätts ingen visibility så behålls default. Kan använda som sätt att forcera visibility false/true
        const featureModifiers: {
            [key: string]: {
                boost: number;
                visibility?: boolean;
            };
        } = {
            trakt: { boost: 10 },
            fastighetsytor: {
                boost: -geometryTypeLevelBase * 10,
                visibility: false
            }
        };

        const sortFeatures = (a: GenericFeatureInfo, b: GenericFeatureInfo): number => {
            const aName = a.name.toLowerCase();
            const bName = b.name.toLowerCase();

            // Sortera och applicera visibility
            const applyModifier = (feature: GenericFeatureInfo, name: string): number => {
                let boost = 0;
                Object.entries(featureModifiers).forEach(([
                    key,
                    value
                ]) => {
                    if (name.includes(key)) {
                        if (value.visibility !== undefined) {
                            feature.visible = value.visibility;
                        }
                        boost = value.boost;
                    }
                });
                return boost;
            };

            const aBoost = applyModifier(a, aName);
            const bBoost = applyModifier(b, bName);

            const aPriority = getPriority(a.geometryType) + aBoost;
            const bPriority = getPriority(b.geometryType) + bBoost;

            // Om samma prioritet så defaultar vi till namnsortering
            if (aPriority === bPriority) {
                return aName.localeCompare(bName);
            }

            return bPriority - aPriority;
        };

        return features
            .filter(x => getPriority(x.geometryType) > 0)
            .sort(sortFeatures);
    }

    private filterEndsWithEmpty(features: GenericFeatureInfo[]): GenericFeatureInfo[] {
        // Tar bort tomma geometrier ifrån listan
        return features.filter(feature => !feature.wktGeometry.trim().endsWith("EMPTY"));
    }

    private updateVisibleGeometries(goto: boolean) {
        this.map.setGeometries(VkKartaLayerNames.importLayer, this.features.filter(f => f.visible).map(this.mapFeatureToGeometry), goto);
    }

    private hideAllGeometries() {
        this.map.setGeometries(VkKartaLayerNames.importLayer, [], false);
    }

    private mapFormToGeometry(form: MapForm): ImportedGeometry {
        return {
            geometryType: form.controls.type.value,
            name: form.controls.name.value,
            wktGeometry: form.controls.wkt.value,
            type: form.controls.destination.value?.geometryType,
            subtype1: form.controls.destination.value?.subtyp1,
            subtype2: form.controls.destination.value?.subtyp2,
            subtype3: form.controls.destination.value?.subtyp3,
            atgardGuid: form.controls.destination.value?.atgGuid
        };
    }

    private getTypeLabel(type: string): string {
        if (this.isTypePunkt(type)) {
            return "Punkt";
        } else if (this.isTypeLinje(type)) {
            return "Linje";
        } else if (this.isTypeYta(type)) {
            return "Yta";
        }
        return type;
    }

    private addMapping(feature: GenericFeatureInfo) {
        const attributesForm = this.fb.array<AttributeForm>([]);
        feature.id = GuidUtils.newGuid();
        feature.attributes.forEach((a) => attributesForm.push(this.fb.group({
            key: [a.key],
            value: [a.value]
        })));

        const mappingForm = this.fb.group({
            name: [feature.name],
            type: [feature.geometryType],
            typeLabel: [this.getTypeLabel(feature.geometryType)],
            wkt: [feature.wktGeometry],
            destination: [],
            atgard: [""],
            attributes: attributesForm,
            id: [feature.id],
            visible: [feature.visible]
        });
        this.mappings.push(mappingForm);
    }

    private getColorForFeature(type: string): any {
        if (this.isTypePunkt(type)) {
            return Colors.yellow50p;
        } else if (this.isTypeLinje(type)) {
            return Colors.magenta50p;
        } else if (this.isTypeYta(type)) {
            return Colors.cyan50p;
        }

        return type;
    }

    private mapFeatureToGeometry(feature: GenericFeatureInfo): IMapGeometry {
        return {
            wkt: feature.wktGeometry,
            visible: true,
            color: feature.mapColor,
            symbolStyle: "circle",
            id: feature.id
        } as IMapGeometryWicket;
    }

    private mapBasdataToKartgeometriMapping(symbolStyle: string, basdata: IKartgeometriBasdataResponse): ImportSelectData[] {
        const geometriMappings: ImportSelectData[] = [];
        if (basdata.subtyper2) {
            basdata.subtyper2.forEach(sub2 => {
                if (basdata.subtyper3 && basdata.subtyper3.some(is3 => is3.namn === sub2)) {
                    const sub3obj = basdata.subtyper3.filter(is3 => is3.namn === sub2)[0];
                    sub3obj.typer.forEach(sub3 => {
                        geometriMappings.push(new ImportSelectData(symbolStyle, basdata.subtyp1, sub2, sub3));
                    });
                } else {
                    geometriMappings.push(new ImportSelectData(symbolStyle, basdata.subtyp1, sub2));
                }
            });
        } else {
            geometriMappings.push(new ImportSelectData(symbolStyle, basdata.subtyp1));
        }

        return geometriMappings;
    }
}
